import { VisualizationType } from '../../../types';
import type { IArtisticRenderer } from './IArtisticRenderer';
import { GrowingCirclesRenderer } from './GrowingCirclesRenderer';
import { ParticleTrailsRenderer } from './ParticleTrailsRenderer';
import { FlowingRibbonsRenderer } from './FlowingRibbonsRenderer';

/**
 * Factory for artistic renderers, keyed by VisualizationType.
 *
 * Each call constructs a fresh renderer instance. Renderers are stateful and
 * per-person, so the AnimationEngine creates and caches one instance per tracked
 * person rather than sharing a single instance.
 *
 * Mapping:
 * - PARTICLE_TRAILS  -> ParticleTrailsRenderer (drifting particle streams)
 * - GEOMETRIC_SHAPES -> GrowingCirclesRenderer (growing-circle body fill)
 * - FLOWING_RIBBONS  -> FlowingRibbonsRenderer (tapering motion ribbons)
 */
/**
 * Mapping for PER_PERSON visualizations only. SCENE-scoped types (e.g.
 * DEPARTURE_BOARD) are built by SceneRendererFactory and intentionally absent
 * here; createArtisticRenderer falls back to the growing-circles renderer if a
 * non-per-person type is ever passed in.
 *
 * Mapping:
 * - PARTICLE_TRAILS  -> ParticleTrailsRenderer (drifting particle streams)
 * - GEOMETRIC_SHAPES -> GrowingCirclesRenderer (growing-circle body fill)
 * - FLOWING_RIBBONS  -> FlowingRibbonsRenderer (tapering motion ribbons)
 */
const RENDERER_BUILDERS: Partial<Record<VisualizationType, () => IArtisticRenderer>> = {
  [VisualizationType.PARTICLE_TRAILS]: () => new ParticleTrailsRenderer(),
  [VisualizationType.GEOMETRIC_SHAPES]: () => new GrowingCirclesRenderer(),
  [VisualizationType.FLOWING_RIBBONS]: () => new FlowingRibbonsRenderer(),
};

/**
 * Create a new artistic renderer for the given visualization type.
 * Falls back to the growing-circles renderer for unknown types.
 */
export function createArtisticRenderer(type: VisualizationType): IArtisticRenderer {
  const builder = RENDERER_BUILDERS[type];
  if (builder) {
    return builder();
  }
  // Fallback for any type without a per-person builder (e.g. a scene-scoped
  // type passed in by mistake): use the growing-circles renderer.
  return new GrowingCirclesRenderer();
}
